import type { CharacterProfile, ChatExportFields } from "@shared/schema";
import type { APIKeyConfig } from "@/types/api-key";
import type { ProviderResponse } from "@/types/usage";
import { parseJsonLike } from './json-utils';
import { parseOpenAIUsage } from "./usage-helpers";

function joinUrl(base: string, path: string): string {
  const b = base.replace(/\/$/, "");
  const p = path.startsWith("/") ? path : `/${path}`;
  return `${b}${p}`;
}

function isGoogleOpenAIEndpoint(baseUrl: string): boolean {
  try {
    const u = new URL(baseUrl);
    return u.hostname.includes('generativelanguage.googleapis.com') && /\/openai\/?$|\/openai\//.test(u.pathname);
  } catch {
    return baseUrl.includes('generativelanguage.googleapis.com') && baseUrl.includes('/openai');
  }
}

function extractJsonObject(text: string): string {
  let t = text.trim();
  const codeBlock = t.match(/^```(?:json)?\s*([\s\S]*?)\s*```$/);
  if (codeBlock) t = codeBlock[1].trim();
  const jsonMatch = t.match(/(\{[\s\S]*\})/);
  if (jsonMatch) t = jsonMatch[1];
  return t;
}

function parseJsonSafe<T>(text: string): T {
  const cleaned = extractJsonObject(text);
  try {
    return JSON.parse(cleaned) as T;
  } catch {
    return cleaned as unknown as T;
  }
}

function vitestDebugLog(...args: unknown[]): void {
  if (typeof process !== 'undefined' && process.env?.VITEST_MANUAL_PROVIDER_DEBUG === 'true') {
    console.log(...args);
  }
}

/**
 * Try to call the /v1/models endpoint to check if it's supported
 * This is a lightweight way to validate API connectivity without generating content
 * @returns { success: boolean; models?: string[] } - success indicates if endpoint responded with 200, models contains available model IDs
 */
export async function tryModelsEndpoint(
  baseUrl: string,
  apiKey: string
): Promise<{ success: boolean; models?: string[] }> {
  try {
    const isGoogle = isGoogleOpenAIEndpoint(baseUrl);
    const needsV1 = !baseUrl.replace(/\/$/, '').endsWith('/v1');
    const path = isGoogle ? "/models" : (needsV1 ? "/v1/models" : "/models");
    const url = joinUrl(baseUrl, path);

    const headers: Record<string, string> = {
      'Authorization': `Bearer ${apiKey}`,
    };
    if (isGoogle) {
      headers['x-goog-api-key'] = apiKey;
    }

    const TIMEOUT_MS = 1500;
    const timeoutPromise = new Promise<'timeout'>((resolve) => setTimeout(() => resolve('timeout'), TIMEOUT_MS));

    try {
      vitestDebugLog('[manual.tryModelsEndpoint] GET', url);
      const fetchPromise = fetch(url, {
        method: 'GET',
        headers,
      });
      const resp: any = await Promise.race([fetchPromise as any, timeoutPromise]);
      if (resp === 'timeout') {
        const result: any = { success: false };
        Object.defineProperty(result, '__reason', { value: 'timeout', enumerable: false });
        return result;
      }

      if (!resp.ok) {
        // 404 means models endpoint not supported, not an error
        if (resp.status === 404) {
          const result: any = { success: false };
          Object.defineProperty(result, '__statusCode', { value: 404, enumerable: false });
          return result;
        }
        // Return hidden status for other HTTP errors so caller can decide
        const result: any = { success: false };
        Object.defineProperty(result, '__statusCode', { value: resp.status, enumerable: false });
        return result;
      }

      let json: any;
      try {
        json = await resp.json();
      } catch {
        // Malformed JSON shouldn't block fallback
        const result: any = { success: false };
        Object.defineProperty(result, '__reason', { value: 'parse', enumerable: false });
        return result;
      }
      
      // Extract model IDs from OpenAI-compatible response
      const models = Array.isArray(json?.data)
        ? json.data.map((m: any) => m.id || m.model).filter(Boolean)
        : [];

      return { success: true, models };
    } catch (e: any) {
      const msg = String(e?.message || '');
      if (msg.includes('AbortError')) {
        const result: any = { success: false };
        Object.defineProperty(result, '__reason', { value: 'timeout', enumerable: false });
        return result;
      }
      if (msg.includes('Failed to fetch') || msg.includes('NetworkError')) {
        const result: any = { success: false };
        Object.defineProperty(result, '__errorMessage', { value: msg, enumerable: false });
        return result;
      }
      return { success: false };
    }
  } catch (e: any) {
    const msg = String(e?.message || '');
    // Propagate network errors so callers can present user-friendly messages
    if (msg.includes('Failed to fetch') || msg.includes('NetworkError')) {
      throw e;
    }
    return { success: false };
  }
}

/**
 * Check if the image generation endpoint exists using OPTIONS or HEAD request
 * This avoids generating an actual image during validation
 * @returns boolean - true if endpoint exists and responds positively
 */
export async function checkImageEndpoint(
  baseUrl: string,
  apiKey: string
): Promise<boolean> {
  try {
    const isGoogle = isGoogleOpenAIEndpoint(baseUrl);
    if (isGoogle) {
      return false; // Google doesn't support image generation via OpenAI endpoint
    }

    const trimmedUrl = baseUrl.replace(/\/$/, '');
    const urlPath = new URL(trimmedUrl.startsWith('http') ? trimmedUrl : `https://${trimmedUrl}`).pathname;
    const needsV1 = urlPath === '' || urlPath === '/';
    const url = joinUrl(baseUrl, needsV1 ? "/v1/images/generations" : "/images/generations");

    const TIMEOUT_MS = 1500;
    const timeoutPromise = new Promise<'timeout'>((resolve) => setTimeout(() => resolve('timeout'), TIMEOUT_MS));

    try {
      // Use OPTIONS or HEAD to check endpoint without generating
      const fetchPromise = fetch(url, {
        method: 'OPTIONS',
        headers: {
          'Authorization': `Bearer ${apiKey}`,
        },
      });
      const resp: any = await Promise.race([fetchPromise as any, timeoutPromise]);
      if (resp === 'timeout') return false;

      // Accept 200, 204, or 405 (method not allowed but endpoint exists)
      return resp.ok || resp.status === 405;
    } catch (e: any) {
      return false;
    }
  } catch (e) {
    return false;
  }
}

/**
 * Parse validation errors into user-friendly messages
 * @param error - The error object or message
 * @returns string - User-friendly error message
 */
export function parseValidationError(error: any): string {
  const errorMessage = String(error?.message || error);

  if (errorMessage.includes('Failed to fetch') || errorMessage.includes('NetworkError')) {
    return 'Failed to connect to endpoint. Check your URL and network connection.';
  }

  if (errorMessage.includes('401') || errorMessage.includes('Unauthorized')) {
    return 'Authentication failed. Check your API key.';
  }

  if (errorMessage.includes('404') || errorMessage.includes('Not Found')) {
    return 'Endpoint not found. Verify your endpoint URL includes the correct path.';
  }

  if (errorMessage.includes('timeout') || errorMessage.includes('AbortError')) {
    return 'Validation request timed out. Check your endpoint availability.';
  }

  return errorMessage;
}

// AI Edit: returns a JSON Merge Patch object describing only the changed fields
export async function editCharacterManual(
  prompts: { system: string; user: string },
  api: Pick<APIKeyConfig, 'apiKey' | 'model' | 'temperature' | 'customEndpoint'>,
  opts?: { signal?: AbortSignal }
): Promise<ProviderResponse<any>> {
  if (!api.customEndpoint) throw new Error('Manual provider requires a custom endpoint');
  const payload = {
    model: api.model,
    temperature: api.temperature ?? 0.7,
    messages: [
      { role: 'system', content: prompts.system },
      { role: 'user', content: prompts.user },
    ],
  };
  const json = await postChatCompletions(api.customEndpoint, api.apiKey, payload, { signal: opts?.signal });
  const usage = parseOpenAIUsage(json);
  const content = String(json?.choices?.[0]?.message?.content ?? '').trim();
  const parsed = parseJsonSafe<any>(content);
  return { value: parsed, usage };
}

async function postChatCompletions(baseUrl: string, apiKey: string, payload: any, opts?: { signal?: AbortSignal }): Promise<any> {
  const isGoogle = isGoogleOpenAIEndpoint(baseUrl);
  // If baseUrl already ends with /v1, don't add it again
  const needsV1 = !baseUrl.replace(/\/$/, '').endsWith('/v1');
  const path = isGoogle ? "/chat/completions" : (needsV1 ? "/v1/chat/completions" : "/chat/completions");
  const url = joinUrl(baseUrl, path);
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };
  // OpenAI-compatible endpoints should honor Authorization: Bearer for compatibility.
  headers['Authorization'] = `Bearer ${apiKey}`;
  if (isGoogle) {
    headers['x-goog-api-key'] = apiKey;
  }
  const resp = await fetch(url, {
    method: 'POST',
    headers,
    body: JSON.stringify(payload),
    signal: opts?.signal,
  });
  if (!resp.ok) {
    let msg = `HTTP ${resp.status}`;
    try {
      const err = await resp.json();
      msg = (err?.error?.message || err?.message || msg);
    } catch {}
    throw new Error(msg);
  }
  return resp.json();
}

// Helper function to convert image URL to data URI
async function urlToDataUri(url: string, signal?: AbortSignal): Promise<string> {
  try {
    const response = await fetch(url, { signal });
    if (!response.ok) {
      throw new Error(`Failed to download image: HTTP ${response.status}`);
    }
    const blob = await response.blob();
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  } catch (error: any) {
    throw new Error(`Failed to convert URL to data URI: ${error.message}`);
  }
}

// Helper function to extract image data from various response formats
function extractImageData(json: any): { b64_json?: string; url?: string; content?: string } | null {
  // Add null/undefined checks at function start
  if (!json || typeof json !== 'object') {
    return null;
  }
  
  // Standard OpenAI format: data[0].b64_json or data[0].url
  // This format may include non-standard fields like cost, paymentSource, remainingBalance
  // We focus on the presence of data[0].b64_json or data[0].url regardless of extra fields
  if (Array.isArray(json.data) && json.data.length > 0) {
    const data = json.data[0];
    if (data && typeof data === 'object') {
      // Check for b64_json field (ignore other fields like cost, paymentSource, etc.)
      if (data.b64_json) return { b64_json: data.b64_json };
      // Check for url field
      if (data.url) return { url: data.url };
    }
  }
  
  // Alternative format: choices[0].message.content (some providers return data URI or markdown)
  if (json?.choices?.[0]?.message?.content) {
    return { content: json.choices[0].message.content };
  }
  
  // Direct b64_json or url at root level (non-standard but some providers use this)
  if (json.b64_json) return { b64_json: json.b64_json };
  if (json.url) return { url: json.url };
  
  return null;
}

async function postImageGenerations(baseUrl: string, apiKey: string, payload: any, opts?: { signal?: AbortSignal }): Promise<any> {
  const isGoogle = isGoogleOpenAIEndpoint(baseUrl);
  if (isGoogle) {
    throw new Error('Image generation is not supported via Google\'s OpenAI-compatible endpoint');
  }
  // Only add /v1 if the base URL has no path or ends with exactly /v1
  const trimmedUrl = baseUrl.replace(/\/$/, '');
  const urlPath = new URL(trimmedUrl.startsWith('http') ? trimmedUrl : `https://${trimmedUrl}`).pathname;
  const needsV1 = urlPath === '' || urlPath === '/';
  const url = joinUrl(baseUrl, needsV1 ? "/v1/images/generations" : "/images/generations");
  const resp = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${apiKey}`,
    },
    body: JSON.stringify(payload),
    signal: opts?.signal,
  });
  if (!resp.ok) {
    let msg = `HTTP ${resp.status}`;
    let errorDetails: any = null;
    try {
      errorDetails = await resp.json();
      // Extract error message from various response formats
      msg = errorDetails?.error?.message || errorDetails?.message || errorDetails?.error || msg;
      
      // Include additional helpful details if available
      if (errorDetails?.requiredBalance !== undefined) {
        msg += ` (Required balance: ${errorDetails.requiredBalance})`;
      }
      if (errorDetails?.status && errorDetails.status !== resp.status) {
        msg += ` [Status: ${errorDetails.status}]`;
      }
    } catch {}
    
    // Create a more informative error message with provider context
    const errorMsg = `Manual provider (${baseUrl}): ${msg} [status ${resp.status}]`;
    throw new Error(errorMsg);
  }
  return resp.json();
}

// Helper function to convert data URI to Blob for multipart upload
function dataUriToBlob(dataUri: string): Blob {
  const parts = dataUri.split(',');
  const mimeMatch = parts[0].match(/:(.*?);/);
  const mime = mimeMatch ? mimeMatch[1] : 'image/png';
  const bstr = atob(parts[1]);
  const n = bstr.length;
  const u8arr = new Uint8Array(n);
  for (let i = 0; i < n; i++) {
    u8arr[i] = bstr.charCodeAt(i);
  }
  return new Blob([u8arr], { type: mime });
}

async function postImageEdits(baseUrl: string, apiKey: string, payload: any, opts?: { signal?: AbortSignal }): Promise<any> {
  const isGoogle = isGoogleOpenAIEndpoint(baseUrl);
  if (isGoogle) {
    throw new Error('Image editing is not supported via Google\'s OpenAI-compatible endpoint');
  }
  
  // Only add /v1 if the base URL has no path or ends with exactly /v1
  const trimmedUrl = baseUrl.replace(/\/$/, '');
  const urlPath = new URL(trimmedUrl.startsWith('http') ? trimmedUrl : `https://${trimmedUrl}`).pathname;
  const needsV1 = urlPath === '' || urlPath === '/';
  const url = joinUrl(baseUrl, needsV1 ? "/v1/images/edits" : "/images/edits");
  
  // Check if we need to use multipart/form-data (when image is a data URI or blob)
  const needsMultipart = payload.image && (
    typeof payload.image === 'string' && payload.image.startsWith('data:')
  );
  
  let body: string | FormData;
  let headers: Record<string, string> = {
    'Authorization': `Bearer ${apiKey}`,
  };
  
  if (needsMultipart) {
    // Use multipart/form-data for image uploads
    const formData = new FormData();
    
    // Convert data URI to blob and append as file
    if (typeof payload.image === 'string' && payload.image.startsWith('data:')) {
      const blob = dataUriToBlob(payload.image);
      formData.append('image', blob, 'image.png');
    }
    
    // Append other fields
    formData.append('prompt', payload.prompt);
    if (payload.model) formData.append('model', payload.model);
    if (payload.n) formData.append('n', String(payload.n));
    if (payload.size) formData.append('size', payload.size);
    if (payload.response_format) formData.append('response_format', payload.response_format);
    
    body = formData;
    // Don't set Content-Type for FormData - browser will set it with boundary
  } else {
    // Use JSON for URL-based images or when provider accepts JSON
    headers['Content-Type'] = 'application/json';
    body = JSON.stringify(payload);
  }
  
  const resp = await fetch(url, {
    method: 'POST',
    headers,
    body,
    signal: opts?.signal,
  });
  
  if (!resp.ok) {
    let msg = `HTTP ${resp.status}`;
    let errorDetails: any = null;
    try {
      errorDetails = await resp.json();
      // Extract error message from various response formats
      msg = errorDetails?.error?.message || errorDetails?.message || errorDetails?.error || msg;
      
      // Include additional helpful details if available
      if (errorDetails?.requiredBalance !== undefined) {
        msg += ` (Required balance: ${errorDetails.requiredBalance})`;
      }
      if (errorDetails?.status && errorDetails.status !== resp.status) {
        msg += ` [Status: ${errorDetails.status}]`;
      }
    } catch {}
    
    // Create a more informative error message with provider context
    const errorMsg = `Manual provider (${baseUrl}): ${msg} [status ${resp.status}]`;
    throw new Error(errorMsg);
  }
  return resp.json();
}

export async function validateManualKey(config: { apiKey: string; model: string; customEndpoint?: string }): Promise<{ isValid: boolean; error?: string; models?: string[] }>{
  const baseUrl = (config.customEndpoint || '').trim();
  if (!baseUrl) return { isValid: false, error: 'Custom endpoint is required for Manual provider' };
  
  try {
    // Try models endpoint first (free, fast)
    const modelsResult: any = await tryModelsEndpoint(baseUrl, config.apiKey);
    if (modelsResult.success) {
      vitestDebugLog('[manual.validateManualKey] models ok for', baseUrl);
      return {
        isValid: true,
        models: modelsResult.models
      };
    }

    // Determine fallback vs. error paths from hidden metadata
    const statusCode = modelsResult?.__statusCode as number | undefined;
    const reason = modelsResult?.__reason as string | undefined;
    const netErr = modelsResult?.__errorMessage as string | undefined;

    if (statusCode === 404 || reason === 'parse') {
      // Fallback to minimal chat completion (max_tokens=1)
      const payload = {
        model: config.model,
        temperature: 0,
        max_tokens: 1,
        messages: [{ role: 'user', content: 'test' }],
      };
      await postChatCompletions(baseUrl, config.apiKey, payload);
      vitestDebugLog('[manual.validateManualKey] fallback chat ok for', baseUrl, reason);
      return { isValid: true };
    }
    if (reason === 'timeout') {
      return { isValid: false, error: parseValidationError(new Error('timeout')) };
    }
    // Authentication or permission errors: surface clear message
    if (statusCode === 401 || statusCode === 403) {
      return { isValid: false, error: parseValidationError(new Error('Unauthorized')) };
    }

    // Network errors: return user-friendly message
    if (netErr) {
      vitestDebugLog('[manual.validateManualKey] network error inferred', netErr);
      return { isValid: false, error: parseValidationError(netErr) };
    }
    // Other errors: return generic connectivity message
    vitestDebugLog('[manual.validateManualKey] unknown models error path', { statusCode, reason });
    return { isValid: false, error: parseValidationError(new Error('Failed to fetch')) };
  } catch (e: any) {
    vitestDebugLog('[manual.validateManualKey] caught error', String(e?.message || e));
    return { isValid: false, error: parseValidationError(e) };
  }
}

export async function validateManualImageKey(config: {
  apiKey: string;
  model: string;
  customEndpoint?: string;
  supportsEdit?: boolean;
}): Promise<{ isValid: boolean; error?: string; capabilities?: { imageEdit?: boolean }; models?: string[] }> {
  const baseUrl = (config.customEndpoint || '').trim();
  
  // Validate endpoint is provided
  if (!baseUrl) {
    return { isValid: false, error: 'Custom endpoint is required for Manual provider' };
  }

  // Validate endpoint format
  try {
    const url = new URL(baseUrl);
    if (!url.protocol.startsWith('http')) {
      return { isValid: false, error: 'Endpoint must use http:// or https://' };
    }
  } catch (e) {
    return { isValid: false, error: 'Invalid endpoint URL format' };
  }

  try {
    // Try models endpoint first (free, fast)
    const modelsResult: any = await tryModelsEndpoint(baseUrl, config.apiKey);
    if (modelsResult.success) {
      return {
        isValid: true,
        capabilities: {
          imageEdit: config.supportsEdit ?? false,
        },
        models: modelsResult.models
      };
    }

    // Authentication or permission errors from models endpoint
    if (modelsResult?.__statusCode === 401 || modelsResult?.__statusCode === 403) {
      return {
        isValid: false,
        error: parseValidationError(new Error('Unauthorized'))
      };
    }

    // If the models endpoint failed due to network issues, surface a clear error
    if (modelsResult?.__errorMessage) {
      return {
        isValid: false,
        error: parseValidationError(modelsResult.__errorMessage)
      };
    }

    // Fallback: Check if images endpoint exists (OPTIONS request)
    let imageEndpointExists: boolean;
    if ((config.supportsEdit === true) && /\/v1\/?$/.test(baseUrl)) {
      // Backward compatibility: some legacy providers expected an extra /v1 in the path when base already included /v1
      imageEndpointExists = await checkImageEndpoint(joinUrl(baseUrl, '/v1'), config.apiKey);
    } else {
      imageEndpointExists = await checkImageEndpoint(baseUrl, config.apiKey);
    }
    if (imageEndpointExists) {
      return {
        isValid: true,
        capabilities: {
          imageEdit: config.supportsEdit ?? false,
        }
      };
    }

    return {
      isValid: false,
      error: 'Unable to verify image generation endpoint. Ensure your endpoint supports OpenAI-compatible image generation.'
    };
  } catch (e: any) {
    return {
      isValid: false,
      error: parseValidationError(e)
    };
  }
}

export async function generateCharacterProfileManual(
  prompts: { system: string; user: string },
  api: Pick<APIKeyConfig, 'apiKey' | 'model' | 'temperature' | 'customEndpoint'>,
  opts?: { signal?: AbortSignal }
): Promise<ProviderResponse<CharacterProfile>> {
  if (!api.customEndpoint) throw new Error('Manual provider requires a custom endpoint');
  const payload = {
    model: api.model,
    temperature: api.temperature ?? 0.7,
    messages: [
      { role: 'system', content: prompts.system },
      { role: 'user', content: prompts.user },
    ],
  };
  const json = await postChatCompletions(api.customEndpoint, api.apiKey, payload, { signal: opts?.signal });
  const usage = parseOpenAIUsage(json);
  const content = String(json?.choices?.[0]?.message?.content ?? '').trim();
  const parsed = parseJsonSafe<any>(content);
  let data = parsed?.character_profile || parsed?.character || parsed;
  // Backward compatibility: if content wasn't JSON, wrap plain text in an object
  if (typeof data === 'string') {
    data = { content: data };
  }
  return { value: data as CharacterProfile, usage, data: data as CharacterProfile } as any;
}

export async function generateChatExportFieldsManual(
  prompts: { system: string; user: string },
  api: Pick<APIKeyConfig, 'apiKey' | 'model' | 'temperature' | 'customEndpoint'>,
  opts?: { signal?: AbortSignal }
): Promise<ProviderResponse<ChatExportFields>> {
  if (!api.customEndpoint) throw new Error('Manual provider requires a custom endpoint');
  const payload = {
    model: api.model,
    temperature: api.temperature ?? 0.7,
    messages: [
      { role: 'system', content: prompts.system },
      { role: 'user', content: prompts.user },
    ],
  };
  const json = await postChatCompletions(api.customEndpoint, api.apiKey, payload, { signal: opts?.signal });
  const usage = parseOpenAIUsage(json);
  const content = String(json?.choices?.[0]?.message?.content ?? '').trim();
  return { value: parseJsonSafe<ChatExportFields>(content), usage };
}

export async function generateImagePlanPromptsManual(
  prompts: { system: string; user: string },
  api: Pick<APIKeyConfig, 'apiKey' | 'model' | 'temperature' | 'customEndpoint'>,
  opts?: { signal?: AbortSignal }
): Promise<ProviderResponse<{ naturalPrompt: string; stableDiffusionPrompt: string; negativePrompt?: string }>> {
  if (!api.customEndpoint) throw new Error('Manual provider requires a custom endpoint');
  const payload = {
    model: api.model,
    temperature: api.temperature ?? 0.7,
    messages: [
      { role: 'system', content: prompts.system },
      { role: 'user', content: prompts.user },
    ],
  };
  const json = await postChatCompletions(api.customEndpoint, api.apiKey, payload, { signal: opts?.signal });
  const usage = parseOpenAIUsage(json);
  const content = String(json?.choices?.[0]?.message?.content ?? '').trim();
  let value: { naturalPrompt: string; stableDiffusionPrompt: string; negativePrompt?: string };
  try {
    value = parseJsonLike<typeof value>(content);
  } catch {
    const out: any = {};
    const lines = content.split(/\r?\n/);
    const keys: Array<'naturalPrompt' | 'stableDiffusionPrompt' | 'negativePrompt'> = ['naturalPrompt', 'stableDiffusionPrompt', 'negativePrompt'];
    for (const key of keys) {
      for (const raw of lines) {
        const line = raw.trim();
        const re = new RegExp(`(^|[\\n\\-*•\\s])"?${key}"?\\s*[:=]\\s*(.+)$`, 'i');
        const m = line.match(re);
        if (m && m[2]) {
          let v = String(m[2]).trim();
          v = v.replace(/[`,]$/, '').replace(/^"|^'|^`/, '').replace(/"$|'$|`$/, '').trim();
          if (v) { out[key] = v; break; }
        }
      }
    }
    if (out.naturalPrompt && !out.stableDiffusionPrompt) out.stableDiffusionPrompt = out.naturalPrompt;
    if (!out.naturalPrompt && out.stableDiffusionPrompt) out.naturalPrompt = out.stableDiffusionPrompt;
    if (!out.naturalPrompt || !out.stableDiffusionPrompt) {
      throw new Error('Provider returned non-JSON without extractable prompts');
    }
    value = out;
  }
  return { value, usage, data: value } as any;
}

export async function rewriteCharacterFieldManual(
  instructions: string,
  api: Pick<APIKeyConfig, 'apiKey' | 'model' | 'temperature' | 'customEndpoint'>,
  opts?: { signal?: AbortSignal }
): Promise<ProviderResponse<string>> {
  if (!api.customEndpoint) throw new Error('Manual provider requires a custom endpoint');
  const payload = {
    model: api.model,
    temperature: api.temperature ?? 0.7,
    messages: [
      { role: 'user', content: instructions },
    ],
  };
  const json = await postChatCompletions(api.customEndpoint, api.apiKey, payload, { signal: opts?.signal });
  const usage = parseOpenAIUsage(json);
  const content = String(json?.choices?.[0]?.message?.content ?? '').trim();
  const codeBlock = content.match(/^```(?:json)?\s*([\s\S]*?)\s*```$/);
  const value = (codeBlock ? codeBlock[1] : content).trim();
  return { value, usage, data: value } as any;
}

export async function generateRoleplayReplyManual(
  prompts: { system: string; messages: Array<{ role: 'user' | 'assistant'; content: string }> },
  api: Pick<APIKeyConfig, 'apiKey' | 'model' | 'temperature' | 'customEndpoint'>,
  opts?: { signal?: AbortSignal }
): Promise<ProviderResponse<string>> {
  if (!api.customEndpoint) throw new Error('Manual provider requires a custom endpoint');
  const payload = {
    model: api.model,
    temperature: api.temperature ?? 0.7,
    messages: [
      { role: 'system', content: prompts.system },
      ...prompts.messages.map((m) => ({ role: m.role, content: m.content })),
    ],
  };
  const json = await postChatCompletions(api.customEndpoint, api.apiKey, payload, { signal: opts?.signal });
  const usage = parseOpenAIUsage(json);
  const content = String(json?.choices?.[0]?.message?.content ?? '').trim();
  return { value: content, usage };
}

// Image generation: uses OpenAI-compatible images API; returns data URI
export async function generateImageManual(
  prompt: string,
  api: Pick<APIKeyConfig, 'apiKey' | 'model' | 'temperature' | 'customEndpoint' | 'supportsImages'>,
  opts?: { signal?: AbortSignal }
): Promise<ProviderResponse<{ imageData: string }>> {
  // Validate configuration
  if (!api.customEndpoint) {
    throw new Error(
      'Manual provider requires a custom endpoint. ' +
      'Please configure your endpoint URL in API Settings.'
    );
  }
  
  if (!api.supportsImages) {
    throw new Error(
      'This manual endpoint is not configured for image generation. ' +
      'Please check your API Settings configuration.'
    );
  }
  
  try {
    const isGptImageModel = /^gpt-image-1(\.5)?$/i.test(api.model || '');
    const payload: any = {
      model: api.model,
      prompt,
      n: 1,
      size: '1024x1024',
    };
    // Only request b64_json for DALL·E-style models; gpt-image-1 doesn't support response_format
    if (!isGptImageModel) {
      payload.response_format = 'b64_json';
    }
    
    const json = await postImageGenerations(api.customEndpoint, api.apiKey, payload, { signal: opts?.signal });
    const usage = parseOpenAIUsage(json);
    
    // Extract image data from various response formats
    const imageData = extractImageData(json);
    
    if (!imageData) {
      throw new Error(
        `No image data returned by manual endpoint at ${api.customEndpoint}. ` +
        'Ensure your endpoint returns OpenAI-compatible image responses with b64_json or url fields.'
      );
    }
    
    // Handle base64-encoded image responses
    if (imageData.b64_json) {
      const b64 = imageData.b64_json;
      if (typeof b64 === 'string' && b64.length > 0) {
        // Add data URI prefix if not already present
        const dataUri = b64.startsWith('data:') ? b64 : `data:image/png;base64,${b64}`;
        return { value: { imageData: dataUri }, usage, data: { imageData: dataUri } } as any;
      }
    }
    
    // Handle URL responses - download and convert to data URI
    if (imageData.url) {
      const url = imageData.url;
      if (typeof url === 'string' && url.length > 0) {
        try {
          const dataUri = await urlToDataUri(url, opts?.signal);
          return { value: { imageData: dataUri }, usage, data: { imageData: dataUri } } as any;
        } catch (error: any) {
          throw new Error(
            `Failed to download image from URL: ${error.message}. ` +
            'The endpoint returned a URL but the image could not be retrieved.'
          );
        }
      }
    }
    
    // Handle content field (markdown or data URI)
    if (imageData.content) {
      const content = imageData.content;
      // Try to extract data URI from content
      const dataUriMatch = content.match(/data:image\/(png|jpe?g|webp);base64,[A-Za-z0-9+/=]+/i);
      if (dataUriMatch) {
        return { value: { imageData: dataUriMatch[0] }, usage, data: { imageData: dataUriMatch[0] } } as any;
      }
      
      // Try to extract URL from markdown image syntax
      const markdownMatch = content.match(/!\[.*?\]\((https?:\/\/[^\)]+)\)/);
      if (markdownMatch) {
        try {
          const dataUri = await urlToDataUri(markdownMatch[1], opts?.signal);
          return { value: { imageData: dataUri }, usage };
        } catch (error: any) {
          throw new Error(
            `Failed to download image from markdown URL: ${error.message}`
          );
        }
      }
    }
    
    throw new Error(
      `Could not extract valid image data from response. ` +
      'The endpoint returned data but it could not be processed as an image.'
    );
  } catch (error: any) {
    // Enhance error messages with endpoint context
    const errorMessage = String(error?.message || error);
    
    // Don't wrap already-enhanced errors (except for 401 where we add explicit context)
    if ((errorMessage.includes('manual endpoint') || errorMessage.includes('API Settings')) && !(errorMessage.includes('401') || errorMessage.includes('Unauthorized'))) {
      throw error;
    }
    
    // Provide context for common errors
    if (errorMessage.includes('Failed to fetch') || errorMessage.includes('NetworkError')) {
      throw new Error(
        `Failed to connect to manual endpoint at ${api.customEndpoint}. ` +
        'Check your endpoint URL and network connection in API Settings.'
      );
    }
    
    if (errorMessage.includes('401') || errorMessage.includes('Unauthorized')) {
      const status = errorMessage.match(/\b(\d{3})\b/)?.[1] || '401';
      throw new Error(
        `Authentication failed with manual endpoint at ${api.customEndpoint} (status ${status}). ` +
        `Manual provider (${api.customEndpoint}): ${errorMessage}`
      );
    }
    
    if (errorMessage.includes('404') || errorMessage.includes('Not Found')) {
      throw new Error(
        `Endpoint not found at ${api.customEndpoint}. ` +
        'Verify your endpoint URL includes the correct path (e.g., /v1/images/generations).'
      );
    }
    
    // Generic error with endpoint context
    throw new Error(
      `Error from manual endpoint at ${api.customEndpoint}: ${errorMessage}. ` +
      'Check your endpoint configuration in API Settings.'
    );
  }
}

// Image editing: uses OpenAI-compatible images/edits API; returns data URI
export async function generateImageEditManual(
  referenceImage: string | string[],
  prompt: string,
  api: Pick<APIKeyConfig, 'apiKey' | 'model' | 'temperature' | 'customEndpoint' | 'supportsImages' | 'supportsEdit'>,
  opts?: { signal?: AbortSignal }
): Promise<ProviderResponse<{ imageData: string }>> {
  // Validate configuration
  if (!api.customEndpoint) {
    throw new Error(
      'Manual provider requires a custom endpoint. ' +
      'Please configure your endpoint URL in API Settings.'
    );
  }
  
  if (!api.supportsImages) {
    throw new Error(
      'This manual endpoint is not configured for image generation. ' +
      'Please check your API Settings configuration.'
    );
  }
  
  if (!api.supportsEdit) {
    throw new Error(
      'Image editing is not enabled for this manual provider. ' +
      'To enable image editing, go to API Settings and check "Supports Image Editing".'
    );
  }
  
  try {
    // For now, use the first reference image if an array is provided
    const imageRef = Array.isArray(referenceImage) ? referenceImage[0] : referenceImage;
    
    const isGptImageModel = /^gpt-image-1(\.5)?$/i.test(api.model || '');
    const payload: any = {
      model: api.model,
      image: imageRef, // Reference image as data URI or URL
      prompt,
      n: 1,
      size: '1024x1024',
    };
    
    // Only request b64_json for DALL·E-style models
    if (!isGptImageModel) {
      payload.response_format = 'b64_json';
    }
    
    const json = await postImageEdits(api.customEndpoint, api.apiKey, payload, { signal: opts?.signal });
    const usage = parseOpenAIUsage(json);
    
    // Extract image data from various response formats
    const imageData = extractImageData(json);
    
    if (!imageData) {
      throw new Error(
        `No image data returned by manual endpoint at ${api.customEndpoint}. ` +
        'Ensure your endpoint returns OpenAI-compatible image responses with b64_json or url fields.'
      );
    }
    
    // Handle base64-encoded image responses
    if (imageData.b64_json) {
      const b64 = imageData.b64_json;
      if (typeof b64 === 'string' && b64.length > 0) {
        // Add data URI prefix if not already present
        const dataUri = b64.startsWith('data:') ? b64 : `data:image/png;base64,${b64}`;
        return { value: { imageData: dataUri }, usage, data: { imageData: dataUri } } as any;
      }
    }
    
    // Handle URL responses - download and convert to data URI
    if (imageData.url) {
      const url = imageData.url;
      if (typeof url === 'string' && url.length > 0) {
        try {
          const dataUri = await urlToDataUri(url, opts?.signal);
          return { value: { imageData: dataUri }, usage, data: { imageData: dataUri } } as any;
        } catch (error: any) {
          throw new Error(
            `Failed to download edited image from URL: ${error.message}. ` +
            'The endpoint returned a URL but the image could not be retrieved.'
          );
        }
      }
    }
    
    // Handle content field (markdown or data URI)
    if (imageData.content) {
      const content = imageData.content;
      // Try to extract data URI from content
      const dataUriMatch = content.match(/data:image\/(png|jpe?g|webp);base64,[A-Za-z0-9+/=]+/i);
      if (dataUriMatch) {
        return { value: { imageData: dataUriMatch[0] }, usage, data: { imageData: dataUriMatch[0] } } as any;
      }
      
      // Try to extract URL from markdown image syntax
      const markdownMatch = content.match(/!\[.*?\]\((https?:\/\/[^\)]+)\)/);
      if (markdownMatch) {
        try {
          const dataUri = await urlToDataUri(markdownMatch[1], opts?.signal);
          return { value: { imageData: dataUri }, usage };
        } catch (error: any) {
          throw new Error(
            `Failed to download edited image from markdown URL: ${error.message}`
          );
        }
      }
    }
    
    throw new Error(
      `Could not extract valid image data from editing response. ` +
      'The endpoint returned data but it could not be processed as an image.'
    );
  } catch (error: any) {
    // Enhance error messages with endpoint context
    const errorMessage = String(error?.message || error);
    
    // Don't wrap already-enhanced errors (except for 401 where we add explicit context)
    if ((errorMessage.includes('manual endpoint') || errorMessage.includes('API Settings')) && !(errorMessage.includes('401') || errorMessage.includes('Unauthorized'))) {
      throw error;
    }
    
    // Provide context for common errors
    if (errorMessage.includes('Failed to fetch') || errorMessage.includes('NetworkError')) {
      throw new Error(
        `Failed to connect to manual endpoint at ${api.customEndpoint}. ` +
        'Check your endpoint URL and network connection in API Settings.'
      );
    }
    
    if (errorMessage.includes('401') || errorMessage.includes('Unauthorized')) {
      const status = errorMessage.match(/\b(\d{3})\b/)?.[1] || '401';
      throw new Error(
        `Authentication failed with manual endpoint at ${api.customEndpoint} (status ${status}). ` +
        `Manual provider (${api.customEndpoint}): ${errorMessage}`
      );
    }
    
    if (errorMessage.includes('404') || errorMessage.includes('Not Found')) {
      throw new Error(
        `Endpoint not found at ${api.customEndpoint}. ` +
        'Verify your endpoint URL includes the correct path (e.g., /v1/images/edits).'
      );
    }
    
    // Generic error with endpoint context
    throw new Error(
      `Error from manual endpoint at ${api.customEndpoint}: ${errorMessage}. ` +
      'Check your endpoint configuration in API Settings.'
    );
  }
}
